import React, { useState } from 'react';
import { flex } from '@fabric-ui/theme';
import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import useQueryObject from '../../../../hooks/useQueryObjectSql';
import { calculatePercentageChange, getLast30DaysDate } from '../../utils';
import PercentageChange from '../percentageChange';
import { getMeanTimeToRespond } from '../queries';
import { FilterContext, SelectedWidgetData, TableDataType } from '../types';
import WidgetTooltip from './WidgetTooltip';

interface MeanTimeToRespondProps {
  filterContext: FilterContext;
  selected?: SelectedWidgetData;
  onSelect?: (selected: SelectedWidgetData) => void;
  closeInteractive?: () => void;
}

const MeanTimeToRespond = (props: MeanTimeToRespondProps) => {
  const { filterContext, selected, onSelect, closeInteractive } = props;
  const theme = useTheme();
  const [currentSelect, setCurrentSelect] = useState<SelectedWidgetData | undefined>(selected);

  const target = 2;

  const renderDot = React.useCallback(
    (dotProps: any) => {
      const { key, ...rest } = dotProps;
      return <CustomizedDot key={key} {...rest} currentSelect={currentSelect?.index} />;
    },
    [currentSelect]
  );

  const { data: meanTimeToRespond, isLoading: isLoadingMeanTimeToRespond } = useQueryObject({
    queryObject: getMeanTimeToRespond(filterContext),
    options: { placeholderData: keepPreviousData },
  });

  const { data: last30Days, isLoading: isLoading30Days } = useQueryObject({
    queryObject: getMeanTimeToRespond({
      ...filterContext,
      dateRange: [getLast30DaysDate(filterContext.dateRange[1]), filterContext.dateRange[1]],
    }),
    options: { placeholderData: keepPreviousData },
  });

  const data = meanTimeToRespond
    ? meanTimeToRespond.map(item => ({
        month: new Date(item.BUCKET_MONTH).toLocaleString('default', { month: 'short' }),
        mttr: item.incident_mttrespond,
      }))
    : [];

  const total = data ? data.reduce((sum, item) => sum + item.mttr, 0) : 0;
  const avg = total / data.length;

  const total30days = last30Days ? last30Days.reduce((sum, item) => sum + item.incident_mttrespond, 0) : 0;
  const avg30days = total30days / (last30Days?.length || 0);

  const percentage = calculatePercentageChange(avg, avg30days);

  if (!currentSelect && closeInteractive) {
    closeInteractive();
  }

  return (
    <Box sx={{ ...flex.col, padding: '16px 24px', background: theme.palette.white.main, height: '100%' }}>
      <Typography sx={{ whiteSpace: 'nowrap', fontSize: '18px', mb: '8px' }} variant="h2">
        Mean Time to Respond (Hours)
      </Typography>
      {!(isLoadingMeanTimeToRespond || isLoading30Days) ? (
        <>
          <CustomLegend avg={formatHours(avg)} target={formatHours(target)} />
          <Box sx={{ ...flex.row, flex: 1, mt: '8px', mr: '16px' }}>
            <Box sx={{ ...flex.col }}>
              <Typography
                variant="h3"
                sx={{
                  mb: 0,
                }}>
                <div>{formatHours(avg)}</div>
              </Typography>
              <div>Average Response Time</div>
              <Box sx={{ ...flex.itemsCenter, gap: '4px', marginTop: '16px' }}>
                <PercentageChange value={percentage} /> last 30 days
              </Box>
            </Box>
            <ResponsiveContainer style={{ flex: 1 }} width="100%" height="100%">
              <LineChart
                data={data}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                onClick={e => {
                  if (e && e.activeTooltipIndex !== undefined) {
                    const index = e.activeTooltipIndex;
                    const selected: SelectedWidgetData = {
                      index,
                      value: data[index],
                      filter: {},
                      tableType: TableDataType.INCIDENT,
                    };
                    if (onSelect) {
                      onSelect(selected);
                    }
                    setCurrentSelect(currentSelect?.index === index ? undefined : selected);
                  }
                }}
                style={{ cursor: 'pointer' }}>
                <CartesianGrid vertical={false} stroke={theme.palette.colors.neutrals[300]} />
                <XAxis dataKey="month" axisLine={false} tickLine={false} />
                <YAxis axisLine={false} tickLine={false} tickFormatter={(value: number) => formatHours(value)} />
                <Tooltip cursor={{ stroke: theme.palette.divider, strokeWidth: 1 }} content={<CustomTooltip />} />
                <Line type="linear" dataKey="mttr" stroke={theme.palette.colors.primary[350]} strokeWidth={2} dot={renderDot} />
                <ReferenceLine y={target} stroke={theme.palette.colors.green[400]} strokeDasharray="8 4" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </>
      ) : (
        <Skeleton variant="rectangular" width="100%" height="100%" />
      )}
    </Box>
  );
};

const formatHours = (hours: number) => {
  if (!hours || hours < 0) return '0h';
  if (hours % 1 === 0) {
    return `${hours}h`;
  }
  return `${hours.toFixed(2)}h`;
};

const CustomLegend = (props: { avg: string; target: string }) => {
  const theme = useTheme();
  const circleSize = 8;

  return (
    <Box sx={{ ...flex.row, gap: '16px', mb: '24px', fontSize: '12px', color: theme.palette.colors.neutrals[500] }}>
      <Box sx={{ ...flex.itemsCenter, gap: 1 }}>
        <Box
          sx={{
            width: circleSize,
            height: circleSize,
            borderRadius: '50%',
            backgroundColor: theme.palette.colors.primary[350],
          }}
        />
        <div>MTTR (avg. {props.avg})</div>
      </Box>
      <Box sx={{ ...flex.itemsCenter, gap: 1 }}>
        <Box sx={{ width: circleSize, height: circleSize, borderRadius: '50%', backgroundColor: theme.palette.colors.green[400] }} />
        <div>Target ({props.target})</div>
      </Box>
    </Box>
  );
};

const CustomizedDot = (props: any) => {
  const theme = useTheme();
  const { cx, cy, index, currentSelect } = props;
  if (index === currentSelect && cx != null && cy != null) {
    return <circle key={index} cx={cx} cy={cy} r={5} fill={theme.palette.colors.primary[500]} />;
  }
  return null;
};

const CustomTooltip = (props: any) => {
  const { active, payload, label } = props;
  const theme = useTheme();

  if (active && payload?.length) {
    const data = payload[0];
    const formattedValue = typeof data.value === 'number' ? formatHours(data.value) : data.value;

    return (
      <WidgetTooltip active={active} payload={payload}>
        <Box sx={{ fontWeight: 600, color: theme.palette.colors.neutrals[100] }}>{label}</Box>
        <Box sx={{ ...flex.justifyBetweenCenter }}>
          <Box sx={{ color: theme.palette.colors.neutrals[350] }}>Avg. Response Time</Box>
          <Box sx={{ fontWeight: 600, color: theme.palette.colors.neutrals[100] }}>{formattedValue}</Box>
        </Box>
      </WidgetTooltip>
    );
  }

  return null;
};

export default MeanTimeToRespond;
